/**
 * Tabela "Por oferta" / "Por plano".
 *
 * As linhas saem das MESMAS fatias que alimentam os KPIs, entao a linha da
 * oferta X e o KPI com o chip X mostram o mesmo inteiro. Clicar no nome da
 * oferta seleciona o chip (e clicar de novo volta para "Tudo") -- a tabela e
 * um segundo caminho para o mesmo filtro, nao um filtro proprio.
 *
 * Pronta para centenas de ofertas por empresa:
 *  - ORDENACAO por qualquer coluna (cabecalho e botao, `aria-sort` no `th`);
 *    a comparacao de taxas usa uma razao SO para ordenar -- nada dela vira
 *    texto;
 *  - BUSCA por nome quando ha mais de 6 ofertas, sem acento e sem caixa;
 *  - "Ver mais" em degraus (5, depois +10) em vez de abrir tudo;
 *  - linha de TOTAL das ofertas listadas (o TOTAL da referencia do dono),
 *    somada em inteiros;
 *  - SINAL automatico por oferta (Oportunidade / Atencao, agregacao.ts), sempre
 *    com texto, e uma explicacao no `title`.
 *
 * Abaixo de `md` vira lista de cartoes com um seletor de ordenacao proprio.
 */

import { useState } from 'react';
import { Badge, Button, Icon, Input, SectionCard, Select } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import { TUDO } from '../agregacao';
import type { FunnelKind, OfferRow, SinalDaOferta } from '../types';
import { VOCABULARIO } from '../vocabulario';

const LIMITE_INICIAL = 5;
const PASSO = 10;
const BUSCA_A_PARTIR_DE = 6;

interface Coluna {
  readonly key: string;
  readonly label: string;
  /** Explicacao em termos de negocio (vira `title` do cabecalho). */
  readonly dica: string;
  readonly valor: (r: OfferRow) => string;
  /** Numero usado SO para ordenar. Razoes ficam aqui e nunca viram texto. */
  readonly numero: (r: OfferRow) => number;
  readonly destaque?: 'good' | 'critical';
}

function razao(part: number, whole: number): number {
  return whole <= 0 ? -1 : part / whole;
}

function colunas(kind: FunnelKind): readonly Coluna[] {
  if (kind === 'expirados') {
    return [
      { key: 'entered', label: 'Venceram', dica: 'Assinaturas deste plano que chegaram ao fim no período sem renovar.', valor: (r) => formatCount(r.entered), numero: (r) => r.entered },
      { key: 'recovered', label: 'Voltaram', dica: 'Voltaram a pagar por causa da régua (botão, e-mail ou vendedora, em até 7 dias).', valor: (r) => formatCount(r.recovered), numero: (r) => r.recovered, destaque: 'good' },
      { key: 'rate', label: 'Taxa de recuperação', dica: 'Voltaram dividido por venceram.', valor: (r) => formatRatio(r.recovered, r.entered), numero: (r) => razao(r.recovered, r.entered) },
      { key: 'amount', label: 'Recuperado', dica: 'Valor pago por quem voltou pela régua.', valor: (r) => formatBRLFromCents(r.recoveredAmountCents), numero: (r) => r.recoveredAmountCents },
      { key: 'alone', label: 'Sozinhos', dica: 'Pagaram de novo antes do primeiro toque; não entram na taxa nem na comissão.', valor: (r) => formatCount(r.returnedAlone), numero: (r) => r.returnedAlone },
      { key: 'seller', label: 'Com o comercial', dica: 'Responderam e a vendedora ainda não fechou. A janela de 7 dias está correndo.', valor: (r) => formatCount(r.withSeller), numero: (r) => r.withSeller },
      { key: 'out', label: 'Ainda fora', dica: 'Venceram e não tiveram desfecho: nem pagaram, nem responderam, nem saíram.', valor: (r) => formatCount(r.stillOut), numero: (r) => r.stillOut, destaque: 'critical' },
      { key: 'stuck', label: 'Parado', dica: 'Valor dos planos de quem ainda está fora.', valor: (r) => formatBRLFromCents(r.stuckAmountCents), numero: (r) => r.stuckAmountCents },
    ];
  }
  return [
    { key: 'checkouts', label: 'Carrinhos', dica: 'Checkouts iniciados desta oferta no período.', valor: (r) => formatCount(r.checkouts), numero: (r) => r.checkouts },
    { key: 'paidOnTime', label: 'Pagaram na hora', dica: 'Pagaram em até 30 minutos. Nunca entram no funil — não há o que recuperar.', valor: (r) => formatCount(r.paidOnTime), numero: (r) => r.paidOnTime },
    { key: 'entered', label: 'Abandonaram', dica: 'Ficaram 30 minutos sem pagar. É a base da taxa de recuperação.', valor: (r) => formatCount(r.entered), numero: (r) => r.entered },
    { key: 'abandonRate', label: 'Taxa de abandono', dica: 'Abandonaram dividido por carrinhos.', valor: (r) => formatRatio(r.entered, r.checkouts), numero: (r) => razao(r.entered, r.checkouts) },
    { key: 'recovered', label: 'Recuperados', dica: 'Pagaram por causa da régua (botão, ligação, e-mail ou vendedora, em até 7 dias).', valor: (r) => formatCount(r.recovered), numero: (r) => r.recovered, destaque: 'good' },
    { key: 'rate', label: 'Taxa de recuperação', dica: 'Recuperados dividido por abandonaram.', valor: (r) => formatRatio(r.recovered, r.entered), numero: (r) => razao(r.recovered, r.entered) },
    { key: 'amount', label: 'Recuperado', dica: 'Valor pago pelas vendas recuperadas.', valor: (r) => formatBRLFromCents(r.recoveredAmountCents), numero: (r) => r.recoveredAmountCents },
    { key: 'seller', label: 'Com o comercial', dica: 'Responderam e a vendedora ainda não fechou. A janela de 7 dias está correndo.', valor: (r) => formatCount(r.withSeller), numero: (r) => r.withSeller },
    { key: 'out', label: 'Ainda parados', dica: 'Abandonaram e não tiveram desfecho: nem pagaram, nem responderam, nem saíram.', valor: (r) => formatCount(r.stillOut), numero: (r) => r.stillOut, destaque: 'critical' },
    { key: 'stuck', label: 'Parado', dica: 'Valor dos carrinhos de quem ainda está parado.', valor: (r) => formatBRLFromCents(r.stuckAmountCents), numero: (r) => r.stuckAmountCents },
  ];
}

const DESTAQUE: Readonly<Record<'good' | 'critical', string>> = {
  good: 'font-semibold text-status-good',
  critical: 'font-semibold text-status-critical',
};

const SINAL: Readonly<Record<SinalDaOferta, { readonly label: string; readonly tone: 'good' | 'warning'; readonly icon: 'raio' | 'alerta'; readonly dica: string }>> = {
  oportunidade: {
    label: 'Oportunidade',
    tone: 'good',
    icon: 'raio',
    dica: 'Mais dinheiro parado que a média das ofertas e taxa de recuperação igual ou acima da geral: vale um toque a mais ou uma condição especial.',
  },
  atencao: {
    label: 'Atenção',
    tone: 'warning',
    icon: 'alerta',
    dica: 'Abandona (ou vence) acima da média e recupera menos da metade da taxa geral: o problema pode estar no checkout ou no plano, não na régua.',
  },
};

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

type Direcao = 'asc' | 'desc';

interface Ordem {
  readonly key: string;
  readonly dir: Direcao;
}

function ordenar(rows: readonly OfferRow[], cols: readonly Coluna[], ordem: Ordem): readonly OfferRow[] {
  if (ordem.key === 'offer') {
    const copia = [...rows].sort((a, b) => a.offer.name.localeCompare(b.offer.name, 'pt-BR'));
    return ordem.dir === 'asc' ? copia : copia.reverse();
  }
  const col = cols.find((c) => c.key === ordem.key);
  if (col === undefined) {
    return rows;
  }
  const sinal = ordem.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const diff = col.numero(a) - col.numero(b);
    return diff === 0 ? a.offer.name.localeCompare(b.offer.name, 'pt-BR') : diff * sinal;
  });
}

function Total({ rows, cols, kind }: { readonly rows: readonly OfferRow[]; readonly cols: readonly Coluna[]; readonly kind: FunnelKind }): JSX.Element {
  const soma: OfferRow = rows.reduce<OfferRow>(
    (acc, r) => ({
      offer: acc.offer,
      checkouts: acc.checkouts + r.checkouts,
      paidOnTime: acc.paidOnTime + r.paidOnTime,
      entered: acc.entered + r.entered,
      recovered: acc.recovered + r.recovered,
      recoveredAmountCents: acc.recoveredAmountCents + r.recoveredAmountCents,
      commissionCents: acc.commissionCents + r.commissionCents,
      returnedAlone: acc.returnedAlone + r.returnedAlone,
      withSeller: acc.withSeller + r.withSeller,
      stillOut: acc.stillOut + r.stillOut,
      stuckAmountCents: acc.stuckAmountCents + r.stuckAmountCents,
      sinal: null,
    }),
    {
      offer: { id: 'total', name: 'Total', priceCents: 0, recurring: false, provider: 'kiwify' },
      checkouts: 0,
      paidOnTime: 0,
      entered: 0,
      recovered: 0,
      recoveredAmountCents: 0,
      commissionCents: 0,
      returnedAlone: 0,
      withSeller: 0,
      stillOut: 0,
      stuckAmountCents: 0,
      sinal: null,
    },
  );
  return (
    <tr className="border-t-2 border-line bg-elevated text-sm">
      <th scope="row" className="px-5 py-3 text-left">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Total</span>
        <span className="block text-xs text-ink-muted">
          {formatCount(rows.length)} {kind === 'expirados' ? (rows.length === 1 ? 'plano' : 'planos') : rows.length === 1 ? 'oferta' : 'ofertas'} listad{rows.length === 1 ? 'a' : 'as'}
        </span>
      </th>
      {cols.map((c) => (
        <td key={c.key} data-numeric="true" className={`px-4 py-3 text-right font-semibold tabular-nums ${c.destaque === undefined ? 'text-ink' : DESTAQUE[c.destaque]}`}>
          {c.valor(soma)}
        </td>
      ))}
    </tr>
  );
}

export function TabelaPorOferta({
  kind,
  rows,
  selecionada,
  onSelecionar,
}: {
  readonly kind: FunnelKind;
  readonly rows: readonly OfferRow[];
  readonly selecionada: string;
  readonly onSelecionar: (offerId: string) => void;
}): JSX.Element {
  const [limite, setLimite] = useState(LIMITE_INICIAL);
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<Ordem>({ key: 'entered', dir: 'desc' });
  const v = VOCABULARIO[kind];
  const cols = colunas(kind);

  const termo = normalizar(busca.trim());
  const filtradas = termo === '' ? rows : rows.filter((r) => normalizar(r.offer.name).includes(termo));
  const ordenadas = ordenar(filtradas, cols, ordem);
  const visiveis = ordenadas.slice(0, limite);
  const escondidas = ordenadas.length - visiveis.length;
  const sinalizadas = rows.filter((r) => r.sinal !== null).length;

  const alternar = (id: string): void => {
    onSelecionar(selecionada === id ? TUDO : id);
  };

  const ordenarPor = (key: string): void => {
    setOrdem((o) => (o.key === key ? { key, dir: o.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: key === 'offer' ? 'asc' : 'desc' }));
  };

  const ariaSort = (key: string): 'ascending' | 'descending' | 'none' => (ordem.key !== key ? 'none' : ordem.dir === 'asc' ? 'ascending' : 'descending');

  // Funcao (nao componente) de proposito: um componente declarado dentro do
  // render ganha identidade nova a cada passagem, o React remonta o <th> e o
  // botao de ordenar perde o foco logo depois do clique.
  const cabecalhoOrdenavel = ({ chave, label, dica, alinhar }: { readonly chave: string; readonly label: string; readonly dica?: string; readonly alinhar: 'left' | 'right' }): JSX.Element => {
    const ativo = ordem.key === chave;
    return (
      <th key={chave} scope="col" aria-sort={ariaSort(chave)} data-numeric={alinhar === 'right' ? 'true' : undefined} className={`px-4 py-3 font-medium ${alinhar === 'right' ? 'text-right' : 'text-left'} first:px-5`}>
        <button
          type="button"
          onClick={() => ordenarPor(chave)}
          title={dica === undefined ? `Ordenar por ${label}` : `${dica} Clique para ordenar.`}
          className={`inline-flex items-center gap-1 rounded transition-colors hover:text-ink ${ativo ? 'text-ink' : 'text-ink-subtle'}`}
        >
          {label}
          <span aria-hidden="true" className={`text-[9px] ${ativo ? 'text-brand' : 'text-ink-subtle/60'}`}>
            {ativo ? (ordem.dir === 'asc' ? '▲' : '▼') : '⇅'}
          </span>
          {ativo ? <span className="sr-only">{ordem.dir === 'asc' ? ', ordem crescente' : ', ordem decrescente'}</span> : null}
        </button>
      </th>
    );
  };

  return (
    <SectionCard
      id="por-oferta"
      title={v.tabelaTitulo}
      icon="modelo"
      description={`Clique ${kind === 'expirados' ? 'num plano' : 'numa oferta'} para recortar a página inteira por ${kind === 'expirados' ? 'ele' : 'ela'}. Clique no cabeçalho para ordenar.`}
      padded={false}
      aside={
        <>
          {sinalizadas > 0 ? (
            <Badge tone="neutral" icon="raio">
              {sinalizadas} com sinal
            </Badge>
          ) : null}
          {rows.length >= BUSCA_A_PARTIR_DE ? (
            <label className="relative block">
              <span className="sr-only">Buscar {v.colunaOferta.toLowerCase()} pelo nome</span>
              <Icon name="busca" size="xs" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
              <Input
                size="sm"
                type="search"
                value={busca}
                onChange={(e) => {
                  setBusca(e.target.value);
                  setLimite(LIMITE_INICIAL);
                }}
                placeholder={`Buscar ${v.colunaOferta.toLowerCase()}…`}
                className="w-56 pl-8"
              />
            </label>
          ) : null}
        </>
      }
    >
      {rows.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-ink-subtle">Nenhuma oferta {v.soRecorrentes ? 'recorrente ' : ''}ativa nesta empresa.</p>
      ) : ordenadas.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-5 pb-6 pt-2 text-center">
          <p className="text-sm text-ink-muted">
            Nenhum{kind === 'expirados' ? ' plano' : 'a oferta'} com “{busca.trim()}” entre {formatCount(rows.length)}.
          </p>
          <Button variant="ghost" size="sm" icon="x" onClick={() => setBusca('')}>
            Limpar busca
          </Button>
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[64rem] border-collapse text-sm">
              <caption className="sr-only">
                {v.tabelaTitulo}: {cols.map((c) => c.label).join(', ')}. Ordenado por {ordem.key === 'offer' ? v.colunaOferta : cols.find((c) => c.key === ordem.key)?.label ?? ''}.
              </caption>
              <thead>
                <tr className="border-b border-line text-xs">
                  {cabecalhoOrdenavel({ chave: 'offer', label: v.colunaOferta, alinhar: 'left' })}
                  {cols.map((c) => cabecalhoOrdenavel({ chave: c.key, label: c.label, dica: c.dica, alinhar: 'right' }))}
                </tr>
              </thead>
              <tbody>
                {visiveis.map((r) => {
                  const ativa = r.offer.id === selecionada;
                  const s = r.sinal === null ? null : SINAL[r.sinal];
                  return (
                    <tr key={r.offer.id} className={`border-b border-line/60 last:border-0 ${ativa ? 'bg-brand/5' : 'hover:bg-ink/5'}`}>
                      <th scope="row" className="px-5 py-3 text-left font-normal">
                        <button type="button" onClick={() => alternar(r.offer.id)} aria-pressed={ativa} className={`text-left transition-colors hover:text-brand ${ativa ? 'font-semibold text-brand' : 'text-ink'}`}>
                          {r.offer.name}
                        </button>
                        <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink-subtle">
                          {formatBRLFromCents(r.offer.priceCents)}
                          {s === null ? null : (
                            <span title={s.dica}>
                              <Badge tone={s.tone} icon={s.icon}>
                                {s.label}
                              </Badge>
                            </span>
                          )}
                        </span>
                      </th>
                      {cols.map((c) => (
                        <td key={c.key} data-numeric="true" className={`px-4 py-3 text-right tabular-nums ${c.destaque === undefined ? 'text-ink' : DESTAQUE[c.destaque]}`}>
                          {c.valor(r)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <Total rows={ordenadas} cols={cols} kind={kind} />
              </tfoot>
            </table>
          </div>

          <div className="flex flex-col gap-3 px-4 md:hidden">
            <label className="flex items-center gap-2 text-xs text-ink-muted">
              <span className="shrink-0">Ordenar por</span>
              <Select
                size="sm"
                value={`${ordem.key}:${ordem.dir}`}
                onChange={(e) => {
                  const [key, dir] = e.target.value.split(':');
                  if (key !== undefined && (dir === 'asc' || dir === 'desc')) {
                    setOrdem({ key, dir });
                  }
                }}
              >
                <option value="offer:asc">{v.colunaOferta} (A–Z)</option>
                {cols.map((c) => (
                  <option key={c.key} value={`${c.key}:desc`}>
                    {c.label} (maior primeiro)
                  </option>
                ))}
              </Select>
            </label>
            <ul className="flex flex-col gap-3">
              {visiveis.map((r) => {
                const ativa = r.offer.id === selecionada;
                const s = r.sinal === null ? null : SINAL[r.sinal];
                return (
                  <li key={r.offer.id} className={`rounded-xl border px-4 py-3 ${ativa ? 'border-brand/50 bg-brand/5' : 'border-line bg-surface'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <button type="button" onClick={() => alternar(r.offer.id)} aria-pressed={ativa} className={`text-left text-sm font-medium ${ativa ? 'text-brand' : 'text-ink'}`}>
                        {r.offer.name}
                      </button>
                      {s === null ? null : (
                        <Badge tone={s.tone} icon={s.icon}>
                          {s.label}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-ink-subtle">{formatBRLFromCents(r.offer.priceCents)}</p>
                    {s === null ? null : <p className="mt-1 text-xs text-ink-muted">{s.dica}</p>}
                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {cols.map((c) => (
                        <div key={c.key} className="flex items-baseline justify-between gap-2 border-b border-dashed border-line/60 py-0.5">
                          <dt className="text-ink-subtle">{c.label}</dt>
                          <dd className={`tabular-nums ${c.destaque === undefined ? 'text-ink' : DESTAQUE[c.destaque]}`}>{c.valor(r)}</dd>
                        </div>
                      ))}
                    </dl>
                  </li>
                );
              })}
            </ul>
          </div>

          {escondidas > 0 || limite > LIMITE_INICIAL ? (
            <div className="flex flex-wrap items-center justify-center gap-2 px-5 py-3">
              {escondidas > 0 ? (
                <Button variant="ghost" size="sm" icon="chevron" onClick={() => setLimite((l) => l + PASSO)}>
                  Ver mais {formatCount(Math.min(PASSO, escondidas))} de {formatCount(escondidas)} {kind === 'expirados' ? (escondidas === 1 ? 'plano' : 'planos') : escondidas === 1 ? 'oferta' : 'ofertas'}
                </Button>
              ) : null}
              {limite > LIMITE_INICIAL ? (
                <Button variant="ghost" size="sm" onClick={() => setLimite(LIMITE_INICIAL)}>
                  Mostrar menos
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="h-4" />
          )}
        </>
      )}
    </SectionCard>
  );
}
